import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AiwynEditorConfig } from '@aiwyn/foundations/components';
import {
  EmailAttachmentPreview,
  EmailContentSource,
  EmailManagementApiService,
  EmailPreviewOverrides,
  EmailPreviewVersion,
  ResendEmailRequest,
  UploadApiService,
} from '@anduin/api';
import { FirmPortalPermission, LaunchDarklyFlag, LaunchDarklyService, PermissionsState } from '@anduin/auth';
import {
  DialogSize,
  FileSource,
  Logger,
  RecipientEmailsComponent,
  extractFailureContext,
  openUrlInNewTab,
  removeDuplicates,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { EMPTY, Observable, catchError, filter, finalize, map, of, switchMap, take, tap } from 'rxjs';
import { OutboundEmailRow } from '../../models/outbound-emails.model';
import { EmailDeliveryStep, EmailDeliveryTrackService } from '../../tracking';

interface ResendEmailModalInput {
  type: 'to' | 'cc' | 'bcc';
  email: string;
}

interface NewEmailAttachment {
  fileId: number;
  fileName: string;
  fileSize: number;
}

export const REBUILD_NOTICE_OPT_OUT_STORAGE_KEY = 'email_delivery_hub.rebuild_resend_notice_opted_out';

const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
// Postmark rejects messages over 10 MB measured after Base64 encoding (~4/3 inflation).
const MAX_MESSAGE_SIZE_BYTES = 10 * 1024 * 1024;

const BLOCKED_ATTACHMENT_EXTENSIONS = new Set([
  'vbs',
  'exe',
  'bin',
  'bat',
  'chm',
  'com',
  'cpl',
  'crt',
  'hlp',
  'hta',
  'inf',
  'ins',
  'isp',
  'jse',
  'lnk',
  'mdb',
  'pcd',
  'pif',
  'reg',
  'scr',
  'sct',
  'shs',
  'vbe',
  'vba',
  'wsf',
  'wsh',
  'wsl',
  'msc',
  'msi',
  'msp',
  'mst',
]);

function base64EncodedSize(bytes: number): number {
  return Math.ceil((bytes * 4) / 3);
}

function isBlankHtml(html: string): boolean {
  return (
    !html ||
    !html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim()
  );
}

export interface ResendEmailModalTracking {
  outboundEmailV4Id?: number;
  outboundEmailId?: number;
  unsentEmailId?: number;
  emailType?: string;
}

export interface ResendEmailModalData {
  mode: 'resend' | 'retry';
  recipients?: ResendEmailModalInput[];
  model?: OutboundEmailRow;
  unsentEmailId?: number;
  canEditRecipients?: boolean;
  emailSettingDisplay?: string | null;
  tracking?: ResendEmailModalTracking;
}

export interface ResendEmailModalResult {
  success: boolean;
  error?: string;
}

@Component({
  standalone: false,
  selector: 'app-resend-email-modal',
  templateUrl: './resend-email-modal.component.html',
  styleUrl: './resend-email-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResendEmailModalComponent {
  private readonly dialogRef = inject(MatDialogRef<ResendEmailModalComponent>);
  private readonly data: ResendEmailModalData = inject(MAT_DIALOG_DATA);
  private readonly emailManagementApiService = inject(EmailManagementApiService);
  private readonly uploadApiService = inject(UploadApiService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly store = inject(Store);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly logger = inject(Logger);
  private readonly trackService = inject(EmailDeliveryTrackService);

  private readonly trackingAction: 'RESEND' | 'RETRY' = this.data.mode === 'retry' ? 'RETRY' : 'RESEND';

  @ViewChild(RecipientEmailsComponent) recipientEmails: RecipientEmailsComponent | undefined;

  private readonly errorAlert = viewChild<ElementRef<HTMLElement>>('errorAlert');

  readonly mode = this.data.mode ?? 'resend';
  readonly isRetryMode = this.mode === 'retry';
  readonly canEditRecipients = this.data.canEditRecipients ?? true;
  readonly emailSettingDisplay = this.data.emailSettingDisplay ?? null;
  readonly title = this.isRetryMode ? 'Retry Email' : 'Resend Email?';
  readonly confirmLabel = 'Yes, ' + (this.isRetryMode ? 'Retry' : 'Resend');
  readonly retryLabel = 'Retry';

  public isResending = signal<boolean>(false);
  public isValidating = signal<boolean>(false);
  public errorMessage = signal<string | null>(null);
  public hasError = computed(() => this.errorMessage() !== null);
  public recipientsFormValid = signal<boolean>(true);

  private confirmedEmitted = false;
  private confirmedAt = 0;

  private readonly _contentEditingFlagEnabled = this.launchDarklyService.getFeatureFlagSignal(
    LaunchDarklyFlag.ENABLE_EMAIL_RESEND_CONTENT_EDITING,
  );
  private readonly _blockedEmailManagementEnabled = this.launchDarklyService.getFeatureFlagSignal(
    LaunchDarklyFlag.ENABLE_BLOCKED_EMAIL_MANAGEMENT,
  );
  private readonly _hasEditContentPermission = this.store.selectSignal(
    PermissionsState.hasPermission([FirmPortalPermission.EMAIL_MANAGEMENT_EDIT_CONTENT]),
  );

  private readonly emailId = this.data.model?.id ?? this.data.model?.outboundEmailId ?? null;
  private readonly emailVersion: EmailPreviewVersion = this.data.model?.id ? 'v4' : 'v3';

  public showEmailContent = computed(() => !this.isRetryMode && this.emailId !== null && this._contentEditingFlagEnabled());
  public canEditContent = computed(() => this.showEmailContent() && this._hasEditContentPermission());
  public showAttachments = computed(() => this.existingAttachments().length > 0 || this.newAttachments().length > 0);

  readonly subjectControl = new FormControl('', { nonNullable: true });
  readonly bodyPrefixControl = new FormControl('', { nonNullable: true });
  private readonly contentForm = new FormGroup({ subject: this.subjectControl, bodyPrefix: this.bodyPrefixControl });
  public contentFormValid = toSignal(this.contentForm.statusChanges.pipe(map((status) => status !== 'INVALID')), { initialValue: true });

  public previewLoading = signal<boolean>(false);
  public previewHtml = signal<SafeHtml | null>(null);
  public contentSource = signal<EmailContentSource | null>(null);
  public rebuildAcknowledged = signal<boolean>(false);
  protected readonly rebuildOptOutTooltip = 'Check the box above to confirm you understand before dismissing this notice.';
  private readonly rebuildNoticeOptedOut = signal<boolean>(this._readRebuildNoticeOptOut());
  public showRebuildNotice = computed(() => this.contentSource() === 'TEMPLATE_REBUILD' && !this.rebuildNoticeOptedOut());
  public existingAttachments = signal<EmailAttachmentPreview[]>([]);
  public newAttachments = signal<NewEmailAttachment[]>([]);
  public attachmentError = signal<string | null>(null);
  public contentErrorMessage = signal<string | null>(null);
  public downloadingFileId = signal<number | null>(null);
  public isSendingTest = signal<boolean>(false);
  public testSendStatus = signal<'success' | 'error' | null>(null);
  private readonly uploadsInFlight = signal<number>(0);
  private inFlightEncodedBytes = 0;
  public isUploadingAttachments = computed(() => this.uploadsInFlight() > 0);
  public isResendDisabled = computed(
    () =>
      (this.canEditContent() && (this.isUploadingAttachments() || !this.contentFormValid())) ||
      (this.showRebuildNotice() && !this.rebuildAcknowledged()),
  );

  private originalSubject = '';

  protected readonly dialogSize = computed(() => (this.showEmailContent() ? DialogSize.LARGE : DialogSize.SMALL));

  protected readonly primaryMessage = computed(() => {
    if (this.isRetryMode) {
      return 'Specify the recipient(s) and retry sending this email.';
    }
    if (this.canEditContent()) {
      return 'Review the email below. You can adjust the subject, add a message above the original content, and attach additional files.';
    }
    switch (this.contentSource()) {
      case 'STORED':
      case 'POSTMARK_FETCH':
        return 'The original email will be resent exactly as it was delivered, without any additions or changes.';
      case 'TEMPLATE_REBUILD':
        return 'A faithful reconstruction of the original email will be sent, generated from the original template and data.';
      default:
        return 'The original email will be resent without changes. If the original content is no longer available, a faithful reconstruction will be generated from the original template and data.';
    }
  });

  protected readonly secondaryMessage = this.isRetryMode
    ? 'Note: upstream data (e.g., the contact record) will still be left unresolved. You may need to update that separately.'
    : 'The email will be sent immediately but will appear on this table after the data has been refreshed.';

  protected readonly editorConfig: AiwynEditorConfig = {
    height: 160,
    placeholder: 'Add an optional message above the original email',
    toolbar: ['bold', 'italic', 'underline', 'bulletList', 'orderedList', 'link'],
    bubbleMenu: false,
  };

  public initialToEmails = computed(() => this._getEmailsByType('to'));
  public initialCcEmails = computed(() => this._getEmailsByType('cc'));
  public initialBccEmails = computed(() => this._getEmailsByType('bcc'));

  public editedToEmails = signal<string[]>(this._getEmailsByType('to'));
  public editedCcEmails = signal<string[]>(this._getEmailsByType('cc'));
  public editedBccEmails = signal<string[]>(this._getEmailsByType('bcc'));
  public suppressedEmails = signal<string[]>([]);
  public pendingUnblockEmails = signal<string[]>([]);
  private lastCheckedEmails = new Set<string>();

  constructor() {
    // Any close without a CONFIRMED emit counts as user-dismissed for funnel accounting.
    // Covers Cancel button, X icon, and backdrop click.
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.confirmedEmitted) {
          this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_DISMISSED, this.buildRowActionMetadata());
        }
      });

    if (!this.isRetryMode && this.emailId !== null) {
      this._loadAttachments();
    }
    // The body is only rendered on the content surface; fetch it (a remote Postmark render) only
    // when that surface is shown. Attachments load regardless — they render for view-only users too.
    if (this.showEmailContent()) {
      this._loadPreview();
    }

    // Disabled recipient controls drop out of the change outputs, so proactively flag any blocked
    // originals — otherwise the user never sees the unblock affordance to act on.
    if (!this.canEditRecipients) {
      this.updateSuppressionCheck(this._allInitialRecipients());
    }
  }

  private _allInitialRecipients(): string[] {
    return removeDuplicates([...this.initialToEmails(), ...this.initialCcEmails(), ...this.initialBccEmails()]);
  }

  private _loadAttachments(): void {
    this.emailManagementApiService
      .getEmailAttachments(this.emailId!, this.emailVersion)
      .pipe(take(1))
      .subscribe({
        next: (attachments) => this.existingAttachments.set(attachments),
        error: (error) => this.logger.error('ResendEmailModalComponent :: Failed to load the email attachments', error),
      });
  }

  private _loadPreview(): void {
    this.previewLoading.set(true);
    this.emailManagementApiService
      .getEmailPreview(this.emailId!, this.emailVersion)
      .pipe(
        take(1),
        finalize(() => this.previewLoading.set(false)),
      )
      .subscribe({
        next: (preview) => {
          this.originalSubject = preview.subject ?? '';
          this.subjectControl.setValue(this.originalSubject);
          this.previewHtml.set(this._sanitizePreviewHtml(preview.htmlBody));
          this.contentSource.set(preview.contentSource ?? null);
          if (this.showRebuildNotice()) {
            this.trackService.trackWorkflow(EmailDeliveryStep.REBUILD_NOTICE_SHOWN, this.buildRowActionMetadata());
          }
        },
        error: (error) => {
          this.logger.error('ResendEmailModalComponent :: Failed to load the email preview', error);
          this.previewHtml.set(null);
          this.contentErrorMessage.set('Failed to load the email preview. Please try again.');
        },
      });
  }

  private _sanitizePreviewHtml(htmlBody: string | null): SafeHtml | null {
    return htmlBody ? this.sanitizer.bypassSecurityTrustHtml(htmlBody) : null;
  }

  refreshPreview(): void {
    if (this.previewLoading()) {
      return;
    }
    this.contentErrorMessage.set(null);
    this.previewLoading.set(true);
    this.emailManagementApiService
      .previewEmailWithOverrides(this.emailId!, this.emailVersion, this._buildOverrides())
      .pipe(
        take(1),
        finalize(() => this.previewLoading.set(false)),
      )
      .subscribe({
        next: (preview) => this.previewHtml.set(this._sanitizePreviewHtml(preview.htmlBody)),
        error: () => this.contentErrorMessage.set('Failed to refresh the email preview. Please try again.'),
      });
  }

  onSendTest(): void {
    if (this.isSendingTest() || this.isUploadingAttachments() || !this.contentFormValid()) {
      return;
    }
    this.testSendStatus.set(null);
    this.isSendingTest.set(true);
    this.emailManagementApiService
      .sendTestEmail(this.emailId!, this.emailVersion, {
        ...this._buildOverrides(),
        newAttachmentFileIds: this.newAttachments().map((attachment) => attachment.fileId),
      })
      .pipe(
        take(1),
        finalize(() => this.isSendingTest.set(false)),
      )
      .subscribe({
        next: () => this.testSendStatus.set('success'),
        error: () => this.testSendStatus.set('error'),
      });
  }

  onAttachmentsSelected(files: FileList | null, fileInput: HTMLInputElement): void {
    const selected = Array.from(files ?? []);
    fileInput.value = '';
    const rejections: string[] = [];
    for (const file of selected) {
      const rejection = this._validateAttachment(file);
      if (rejection) {
        rejections.push(rejection);
      } else {
        this._uploadAttachment(file);
      }
    }
    this.attachmentError.set(rejections.length ? rejections.join(' ') : null);
  }

  private _validateAttachment(file: File): string | null {
    const dotIndex = file.name.lastIndexOf('.');
    const extension = dotIndex >= 0 ? file.name.slice(dotIndex + 1).toLowerCase() : '';
    if (BLOCKED_ATTACHMENT_EXTENSIONS.has(extension)) {
      return `${file.name} cannot be attached because .${extension} files are not allowed.`;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      return `${file.name} exceeds the 5 MB attachment limit.`;
    }
    if (this._encodedAttachmentBytes() + base64EncodedSize(file.size) > MAX_MESSAGE_SIZE_BYTES) {
      return `${file.name} would exceed the 10 MB maximum message size.`;
    }
    return null;
  }

  private _encodedAttachmentBytes(): number {
    return this.newAttachments().reduce((total, attachment) => total + base64EncodedSize(attachment.fileSize), this.inFlightEncodedBytes);
  }

  private _uploadAttachment(file: File): void {
    const encodedSize = base64EncodedSize(file.size);
    this.inFlightEncodedBytes += encodedSize;
    this.uploadsInFlight.update((count) => count + 1);
    this.uploadApiService
      .fileUpload({ file, fileSource: FileSource.EMAIL_ATTACHMENT, disableFormData: true })
      .pipe(
        filter(({ httpEvent }) => httpEvent.type === HttpEventType.Response && httpEvent.ok),
        take(1),
        switchMap(({ fileMutationResponse }) =>
          this.uploadApiService.updateFileStatus(fileMutationResponse.fileId).pipe(map(() => fileMutationResponse.fileId)),
        ),
        finalize(() => {
          this.inFlightEncodedBytes -= encodedSize;
          this.uploadsInFlight.update((count) => count - 1);
        }),
      )
      .subscribe({
        next: (fileId) => this.newAttachments.update((list) => [...list, { fileId, fileName: file.name, fileSize: file.size }]),
        error: () => this.attachmentError.set(`${file.name} failed to upload. Please try again.`),
      });
  }

  removeNewAttachment(fileId: number): void {
    this.newAttachments.update((list) => list.filter((attachment) => attachment.fileId !== fileId));
  }

  downloadAttachment(attachment: EmailAttachmentPreview): void {
    if (this.downloadingFileId() !== null) {
      return;
    }
    this.contentErrorMessage.set(null);
    this.downloadingFileId.set(attachment.fileId);
    this.emailManagementApiService
      .getEmailAttachmentDownloadUrl(this.emailId!, attachment.fileId, this.emailVersion)
      .pipe(
        take(1),
        finalize(() => this.downloadingFileId.set(null)),
      )
      .subscribe({
        next: (result) => {
          if (result?.url) {
            openUrlInNewTab(result.url);
          } else {
            this.contentErrorMessage.set(`A download link for ${attachment.filename} is not available.`);
          }
        },
        error: () => this.contentErrorMessage.set(`Failed to download ${attachment.filename}. Please try again.`),
      });
  }

  private _buildOverrides(): EmailPreviewOverrides {
    return {
      subjectOverride: this._subjectOverride(),
      htmlBodyPrefixOverride: this._htmlBodyPrefixOverride(),
    };
  }

  private _subjectOverride(): string | null {
    const value = this.subjectControl.value.trim();
    return value && value !== this.originalSubject.trim() ? value : null;
  }

  private _htmlBodyPrefixOverride(): string | null {
    const html = this.bodyPrefixControl.value;
    return isBlankHtml(html) ? null : html;
  }

  private _getEmailsByType(type: 'to' | 'cc' | 'bcc'): string[] {
    return (this.data?.recipients ?? []).filter((r) => r.type === type).map((r) => r.email);
  }

  private updateSuppressionCheck(emails: string[]): void {
    // Flag off: the blocked-address list isn't tenant-scoped yet, so skip the /check call and fail
    // open — no suppression warnings, send ungated. Restored once the backend is scoped.
    if (!this._blockedEmailManagementEnabled()) {
      this.suppressedEmails.set([]);
      return;
    }

    const uniqueEmails = removeDuplicates(emails);
    const normalizedSet = new Set(uniqueEmails.map((e) => e.trim().toLowerCase()));

    if (!uniqueEmails.length) {
      this.suppressedEmails.set([]);
      this.lastCheckedEmails = normalizedSet;
      return;
    }

    const isSubset = uniqueEmails.every((e) => this.lastCheckedEmails.has(e.trim().toLowerCase()));
    if (isSubset) {
      this.suppressedEmails.update((prev) => prev.filter((s) => normalizedSet.has(s.toLowerCase())));
      this.lastCheckedEmails = normalizedSet;
      return;
    }

    this.lastCheckedEmails = normalizedSet;
    this.emailManagementApiService
      .checkBlockedEmailAddresses(uniqueEmails)
      .pipe(
        take(1),
        map((result) => result.map((s) => s.emailAddress)),
        catchError(() => of([])),
      )
      .subscribe((suppressed) => this.suppressedEmails.set(suppressed));
  }

  onPrimaryEmailsChanged(emails: string[]): void {
    this.editedToEmails.set(emails);
    const all = [...emails, ...this.editedCcEmails(), ...this.editedBccEmails()];
    this.cleanStalePendingUnblocks(all);
    this.updateSuppressionCheck(all);
  }

  onCcEmailsChanged(emails: string[]): void {
    this.editedCcEmails.set(emails);
    const all = [...this.editedToEmails(), ...emails, ...this.editedBccEmails()];
    this.cleanStalePendingUnblocks(all);
    this.updateSuppressionCheck(all);
  }

  onBccEmailsChanged(emails: string[]): void {
    this.editedBccEmails.set(emails);
    const all = [...this.editedToEmails(), ...this.editedCcEmails(), ...emails];
    this.cleanStalePendingUnblocks(all);
    this.updateSuppressionCheck(all);
  }

  private cleanStalePendingUnblocks(currentEmails: string[]): void {
    const current = new Set(currentEmails.map((e) => e.trim().toLowerCase()));
    const stale = this.pendingUnblockEmails().filter((e) => !current.has(e));
    if (stale.length > 0) {
      this.pendingUnblockEmails.update((list) => list.filter((e) => current.has(e)));
    }
  }

  onRecipientsValidChanged(isValid: boolean): void {
    this.recipientsFormValid.set(isValid);
  }

  onUnblockRequested(email: string): void {
    this.pendingUnblockEmails.update((list) => [...list, email]);
  }

  onUndoUnblockRequested(email: string): void {
    this.pendingUnblockEmails.update((list) => list.filter((e) => e !== email));
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  onRebuildNoticeOptOut(): void {
    // Opting out hides the notice and un-gates Resend, so it must not bypass the acknowledgement
    // — mirrors the disabled button and holds even if this is called directly.
    if (!this.rebuildAcknowledged()) {
      return;
    }
    this.trackService.trackWorkflow(EmailDeliveryStep.REBUILD_NOTICE_OPTED_OUT, this.buildRowActionMetadata());
    this.rebuildNoticeOptedOut.set(true);
    try {
      localStorage.setItem(REBUILD_NOTICE_OPT_OUT_STORAGE_KEY, 'true');
    } catch {
      // Storage may be unavailable (e.g. private browsing); the opt-out then lasts for this dialog only.
    }
  }

  private _readRebuildNoticeOptOut(): boolean {
    try {
      return localStorage.getItem(REBUILD_NOTICE_OPT_OUT_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  onResend(): void {
    if (this.isResending() || this.isValidating() || this.isResendDisabled()) {
      return;
    }

    this.errorMessage.set(null);
    this.isValidating.set(true);

    this._validateRecipients()
      .pipe(
        take(1),
        finalize(() => this.isValidating.set(false)),
      )
      .subscribe(({ isFormValid, hasUnresolvedBlocked }) => {
        if (hasUnresolvedBlocked) {
          this._showError('One or more recipients are blocked. Unblock or remove them before sending.');
          return;
        }
        if (!isFormValid) {
          this._showError('Please correct the recipient fields before sending.');
          return;
        }
        this._performResend();
      });
  }

  private _validateRecipients(): Observable<{ isFormValid: boolean; hasUnresolvedBlocked: boolean }> {
    // Disabled controls drop out of the edited* signals, so check the originals instead — the same
    // recipients still go out, and any blocked one must be surfaced for unblocking.
    const allEmails = this.canEditRecipients
      ? removeDuplicates([...this.editedToEmails(), ...this.editedCcEmails(), ...this.editedBccEmails()])
      : this._allInitialRecipients();
    const pending = new Set(this.pendingUnblockEmails().map((e) => e.toLowerCase()));
    const suppressedSet = new Set(this.suppressedEmails().map((e) => e.toLowerCase()));
    const allAlreadyBlocked = allEmails.length > 0 && allEmails.every((e) => suppressedSet.has(e.trim().toLowerCase()));

    let blockedCheck$: Observable<string[]>;
    // Flag off: emit (don't skip) an empty result so onResend's awaited check completes instead of
    // hanging, leaving the send ungated while the backend list isn't tenant-scoped.
    if (!this._blockedEmailManagementEnabled()) {
      blockedCheck$ = of<string[]>([]);
    } else if (allEmails.length === 0) {
      blockedCheck$ = of<string[]>([]);
    } else if (allAlreadyBlocked) {
      blockedCheck$ = of<string[]>(this.suppressedEmails());
    } else {
      blockedCheck$ = this.emailManagementApiService.checkBlockedEmailAddresses(allEmails).pipe(
        map((result) => result.map((s) => s.emailAddress)),
        catchError(() => of<string[]>([])),
      );
    }

    return blockedCheck$.pipe(
      tap((blocked) => {
        this.suppressedEmails.set(blocked);
        this.lastCheckedEmails = new Set(allEmails.map((e) => e.trim().toLowerCase()));
      }),
      switchMap((blocked) => {
        const hasUnresolvedBlocked = blocked.some((b) => !pending.has(b.toLowerCase()));
        // validate() reports a disabled recipient array as invalid; with editing off the fields
        // can't change, so only the blocked-address check gates the send. Unblocking keeps the
        // same recipients, so it isn't editing.
        if (!this.canEditRecipients) {
          return of({ isFormValid: true, hasUnresolvedBlocked });
        }
        const formValidation$ = this.recipientEmails?.validate() ?? of(this.recipientsFormValid());
        return formValidation$.pipe(
          take(1),
          map((isFormValid) => ({ isFormValid, hasUnresolvedBlocked })),
        );
      }),
    );
  }

  private _performResend(): void {
    this.isResending.set(true);
    this.errorMessage.set(null);
    this.confirmedEmitted = true;
    this.confirmedAt = performance.now();
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUBMITTED, this.buildRowActionMetadata());

    const toUnblock = this.pendingUnblockEmails();
    const unblock$ =
      toUnblock.length > 0
        ? this.emailManagementApiService.removeBlockedEmailAddresses(toUnblock).pipe(catchError(() => of(null)))
        : of(null);

    const action$ = this.isRetryMode
      ? this.emailManagementApiService.retryUnsentEmail(this.data.unsentEmailId!, {
          to: this.editedToEmails(),
          cc: this.editedCcEmails(),
          bcc: this.editedBccEmails(),
        })
      : this.emailManagementApiService.resendEmail(this._buildResendEmailRequest(this.data.model!));

    unblock$
      .pipe(
        take(1),
        switchMap(() => action$),
        tap(() => this.logger.info(`ResendEmailModalComponent :: Email ${this.isRetryMode ? 'retried' : 'resent'} successfully`)),
        tap(() => this.emitRowActionOutcome(EmailDeliveryStep.ROW_ACTION_SUCCEEDED)),
        catchError((error) => this.handleEmailResendFailure(error)),
        finalize(() => this.isResending.set(false)),
      )
      .subscribe(() => {
        this.dialogRef.close({ success: true } as ResendEmailModalResult);
      });
  }

  private buildRowActionMetadata(): {
    action: 'RESEND' | 'RETRY';
    outboundEmailV4Id?: number;
    outboundEmailId?: number;
    unsentEmailId?: number;
    emailType?: string;
    contentSource?: EmailContentSource;
  } {
    const tracking = this.data.tracking ?? {};
    return {
      action: this.trackingAction,
      outboundEmailV4Id: tracking.outboundEmailV4Id,
      outboundEmailId: tracking.outboundEmailId,
      unsentEmailId: tracking.unsentEmailId ?? this.data.unsentEmailId,
      emailType: tracking.emailType,
      contentSource: this.contentSource() ?? undefined,
    };
  }

  private emitRowActionOutcome(step: EmailDeliveryStep.ROW_ACTION_SUCCEEDED): void;
  private emitRowActionOutcome(step: EmailDeliveryStep.ROW_ACTION_FAILED, error: unknown): void;
  private emitRowActionOutcome(step: EmailDeliveryStep.ROW_ACTION_SUCCEEDED | EmailDeliveryStep.ROW_ACTION_FAILED, error?: unknown): void {
    const base = this.buildRowActionMetadata();
    const operationDurationMs = performance.now() - this.confirmedAt;
    if (step === EmailDeliveryStep.ROW_ACTION_FAILED) {
      this.trackService.trackWorkflow(step, { ...base, ...extractFailureContext(error), operationDurationMs });
    } else {
      this.trackService.trackWorkflow(step, { ...base, operationDurationMs });
    }
  }

  private _buildResendEmailRequest(model: OutboundEmailRow): ResendEmailRequest {
    if (!model.outboundEmailId && !model.id) {
      this.logger.warn('ResendEmailModalComponent :: Neither outboundEmailId nor id is present on email model');
    }

    const toRecipients = this.editedToEmails();
    const ccRecipients = this.editedCcEmails();
    const bccRecipients = this.editedBccEmails();

    const request: ResendEmailRequest = {
      ...(model.id ? { outboundEmailV4Id: model.id } : { outboundEmailId: model.outboundEmailId }),
      recipients: toRecipients.length ? toRecipients : undefined,
      cc: ccRecipients.length ? ccRecipients : undefined,
      bcc: bccRecipients.length ? bccRecipients : undefined,
    };

    if (!this.canEditContent()) {
      return request;
    }

    const subject = this._subjectOverride();
    const htmlBodyPrefix = this._htmlBodyPrefixOverride();
    const newAttachmentFileIds = this.newAttachments().map((attachment) => attachment.fileId);

    return {
      ...request,
      ...(subject ? { subject } : {}),
      ...(htmlBodyPrefix ? { htmlBodyPrefix } : {}),
      ...(newAttachmentFileIds.length ? { newAttachmentFileIds } : {}),
    };
  }

  private handleEmailResendFailure<T extends Error>(error: T): Observable<never> {
    this.logger.error(`ResendEmailModalComponent :: Error ${this.isRetryMode ? 'retrying' : 'resending'} email`, error);
    this.emitRowActionOutcome(EmailDeliveryStep.ROW_ACTION_FAILED, error);
    // Dialog stays open after failure so the user can retry or dismiss. Re-open the dismiss
    // funnel by clearing `confirmedEmitted` — otherwise a post-failure close emits no terminal event.
    this.confirmedEmitted = false;
    const payload = error instanceof HttpErrorResponse ? (error.error as { message?: string; error?: string } | null) : null;
    const rawServerMessage = payload?.message ?? payload?.error;
    const serverMessage = rawServerMessage ? this._formatServerErrorMessage(rawServerMessage) : undefined;
    this._showError(
      serverMessage ??
        (this.isRetryMode ? 'Failed to retry the email record. Please try again.' : 'Failed to resend email. Please try again.'),
    );
    return EMPTY;
  }

  private _showError(message: string): void {
    this.errorMessage.set(message);
    setTimeout(() => this.errorAlert()?.nativeElement.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
  }

  private _formatServerErrorMessage(message: string): string {
    const withoutId = message.replace(/\s*\(id=[^)]*\)/g, '').trim();
    return withoutId.endsWith('.') ? withoutId : `${withoutId}.`;
  }

  retry(): void {
    this.onResend();
  }
}
